import React, { createContext, useState, useEffect, useContext } from 'react';
import { api, errorMessage, getToken, getOAuthSessionToken } from '../lib/api';
import { trackSignUp, trackLogin, trackLogout, trackDealClaim, setUserProperties } from '../utils/analytics';

/**
 * Session keys this context parks in sessionStorage. logout() must clear all of
 * them: a shared tab used to hand the next visitor the previous user's pending
 * claim and preview hub (audit row 114).
 */
const SESSION_KEYS = [
  'postAuthRedirect',
  'pendingClaim',
  'preview_hub_id',
  'preview_hub_name',
  'pending_business_invite_token',
  'pending_business_invite_business_id',
  'tappd:wallet-token', // guest wallet link kept for the tab (pages/public/Wallet.jsx)
  // Slot model (Phase 7b). Literals, not imports: the slot pages are lazy chunks.
  'tappd_slot_checkout_next', // second Stripe session of a Sponsor cart (pages/business/slot/billingData.js)
  'tappd_slot_onboarding_draft', // onboarding draft (pages/business/slot/onboardingModel.js)
  // Site redesign Phase 5: public form drafts (names, emails, phone numbers). Literals too.
  'tappd_venue_apply_draft', // venue application draft (pages/public/venueApplyModel.js)
  'tappd:free-listing-draft', // free listing draft (pages/public/FreeListingSignup.jsx)
];

/** POST /auth/signup 202 for a passwordless guest's email (slot model, Phase 6c). */
export const VERIFICATION_SENT_MESSAGE = 'Check your email to set your password';

/** OAuth sessions ride on a cookie, so those calls keep withCredentials. */
const withCookies = { withCredentials: true };

/** A JWT or an OAuth session is enough to try an authenticated call. */
const hasSession = () => !!getToken() || !!getOAuthSessionToken();

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [postAuthRedirect, setPostAuthRedirectState] = useState(
    () => sessionStorage.getItem('postAuthRedirect') || null
  );

  // Sync postAuthRedirect to sessionStorage so it survives page reloads (Google OAuth)
  const setPostAuthRedirect = (url) => {
    setPostAuthRedirectState(url);
    if (url) {
      sessionStorage.setItem('postAuthRedirect', url);
    } else {
      sessionStorage.removeItem('postAuthRedirect');
    }
  };

  const clearPostAuthRedirect = () => {
    setPostAuthRedirectState(null);
    sessionStorage.removeItem('postAuthRedirect');
  };

  // Pending claim state for preview mode (claim deferred until after auth)
  const [pendingClaim, setPendingClaimState] = useState(
    () => {
      const stored = sessionStorage.getItem('pendingClaim');
      return stored ? JSON.parse(stored) : null;
    }
  );

  const setPendingClaim = (claim) => {
    setPendingClaimState(claim);
    if (claim) {
      sessionStorage.setItem('pendingClaim', JSON.stringify(claim));
    } else {
      sessionStorage.removeItem('pendingClaim');
    }
  };

  const clearPendingClaim = () => {
    setPendingClaimState(null);
    sessionStorage.removeItem('pendingClaim');
  };

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Either an email/password JWT or an OAuth session is enough to try.
      if (hasSession()) {
        try {
          const response = await api.get('/auth/me', withCookies);
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Clear the invalid credential and fall back to signed out.
          localStorage.removeItem('token');
          localStorage.removeItem('oauth_session_token');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      if (hasSession()) {
        const response = await api.get('/auth/me', withCookies);
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // A failed refresh leaves the existing session state alone.
    }
  };

  const signup = async (emailOrData, password, first_name = '', last_name = '') => {
    try {
      // Support both object and individual parameters
      let signupData;
      if (typeof emailOrData === 'object') {
        signupData = {
          email: emailOrData.email,
          password: emailOrData.password,
          first_name: emailOrData.first_name || '',
          last_name: emailOrData.last_name || '',
          referral_code: emailOrData.referral_code || null,
          invite_token: emailOrData.invite_token || null
        };
      } else {
        signupData = {
          email: emailOrData,
          password,
          first_name,
          last_name
        };
      }

      // Check for pending business invite token in sessionStorage
      const pendingInviteToken = sessionStorage.getItem('pending_business_invite_token');
      if (pendingInviteToken && !signupData.invite_token) {
        signupData.invite_token = pendingInviteToken;
      }

      const response = await api.post('/auth/signup', signupData, withCookies);

      // Slot model (Phase 6c): an email that already holds codes from an email-only claim
      // (a passwordless guest) gets 202 {status: "verification_sent"} and a set-password
      // email instead of a session. Not an error, and no token: the caller shows
      // "Check your email to set your password". Only the SLOT_MODEL backend sends it.
      if (response.status === 202 || response.data?.status === 'verification_sent') {
        return {
          success: false,
          verificationSent: true,
          message: response.data?.message || VERIFICATION_SENT_MESSAGE,
        };
      }
      const { access_token, referral_bonus, welcome_bonus, business_linked } = response.data;

      // Clear the pending invite token if signup was successful
      if (pendingInviteToken) {
        sessionStorage.removeItem('pending_business_invite_token');
      }

      // Store token
      localStorage.setItem('token', access_token);

      // Get user data
      const userResponse = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${access_token}` },
        withCredentials: true
      });

      setUser(userResponse.data);
      setIsAuthenticated(true);
      trackSignUp({ method: 'email', referral: !!(referral_bonus), businessLinked: !!(business_linked) });
      setUserProperties({ userId: userResponse.data.id, role: userResponse.data.role });
      return { success: true, referral_bonus: referral_bonus || 0, welcome_bonus: welcome_bonus || 0, business_linked: business_linked || false };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Signup failed. Please try again.') };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password }, withCookies);

      const { access_token } = response.data;

      // Store token
      localStorage.setItem('token', access_token);

      // Get user data
      const userResponse = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${access_token}` },
        withCredentials: true
      });

      setUser(userResponse.data);
      setIsAuthenticated(true);
      trackLogin({ method: 'email' });
      setUserProperties({ userId: userResponse.data.id, role: userResponse.data.role });

      return { success: true };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Invalid credentials. Please try again.') };
    }
  };

  const logout = () => {
    trackLogout();
    localStorage.removeItem('token');
    localStorage.removeItem('oauth_session_token');
    // Portal credentials stored outside this context must not survive sign-out
    localStorage.removeItem('businessOwnerToken');
    localStorage.removeItem('adminToken');
    // Clear session cookie
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // Drop every per-session key too — on a shared tab these used to survive
    // logout and hand the next visitor the previous user's pending claim.
    SESSION_KEYS.forEach((key) => {
      try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
    });
    setPendingClaim(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode); // Set the mode (login or signup)
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  // Update profile (triggers refresh)
  const updateProfile = async (profileData) => {
    try {
      await api.put('/auth/profile', profileData);

      // Refresh user data after update
      await refreshUser();
      return { success: true };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Failed to update profile') };
    }
  };

  const getMyClaims = async () => {
    try {
      if (!hasSession()) {
        return { success: false, data: [] };
      }

      const response = await api.get('/deals/my-claims', withCookies);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  const claimDeal = async (dealData) => {
    try {
      if (!hasSession()) {
        return { success: false, error: 'Please login to claim deals' };
      }

      const response = await api.post('/deals/claim', dealData, withCookies);

      trackDealClaim({
        dealId: dealData.deal_id,
        dealTitle: dealData.deal_title,
        businessId: dealData.business_id,
        businessName: dealData.business_name
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Failed to claim deal') };
    }
  };

  // Complete pending claim after authentication (for preview mode)
  const completePendingClaim = async () => {
    const pending = pendingClaim;
    const hubToUnlock = sessionStorage.getItem('preview_hub_id');

    if (!pending && !hubToUnlock) {
      return { success: false, noPending: true };
    }

    try {
      // Step 1: Unlock the preview hub if exists
      if (hubToUnlock) {
        try {
          await api.post(`/hubs/unlock/${hubToUnlock}`, {}, withCookies);
          // Refresh user to update unlocked_hubs
          await refreshUser();
        } catch (unlockError) {
          // Continue even if unlock fails (might already be unlocked)
        }
      }

      // Step 2: Complete the pending claim if exists
      if (pending) {
        const result = await claimDeal(pending);
        clearPendingClaim();

        // Clear preview state
        sessionStorage.removeItem('preview_hub_id');
        sessionStorage.removeItem('preview_hub_name');

        return result;
      }

      // Clear preview state even if no claim
      sessionStorage.removeItem('preview_hub_id');
      sessionStorage.removeItem('preview_hub_name');

      return { success: true, unlockOnly: true };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Could not finish claiming that deal.') };
    }
  };

  const checkDealClaim = async (dealId) => {
    try {
      if (!hasSession()) {
        return { claimed: false };
      }

      const response = await api.get(`/deals/check/${dealId}`, withCookies);
      return response.data;
    } catch (error) {
      return { claimed: false };
    }
  };

  const getMyDealClaims = async () => {
    try {
      if (!hasSession()) {
        return { success: false, data: [] };
      }

      const response = await api.get('/deals/my-claims', withCookies);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  // Role checking helpers for unified auth
  const isAdmin = () => user?.role === 'admin';
  const isAdvertiser = () => user?.role === 'advertiser';
  const isConsumer = () => user?.role === 'consumer' || !user?.role; // Default to consumer

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  // Process OAuth session after redirect from Emergent Auth
  const processOAuthSession = async (sessionId) => {
    try {
      const response = await api.post('/auth/oauth/process-session', {}, {
        headers: {
          'X-Session-ID': sessionId
        },
        withCredentials: true
      });

      const { user: userData, session_token, account_linked } = response.data;

      // Store session token for OAuth authentication
      localStorage.setItem('oauth_session_token', session_token);

      // Set user data and authentication state
      setUser(userData);
      setIsAuthenticated(true);

      return {
        success: true,
        user: userData,
        account_linked
      };
    } catch (error) {
      console.error('OAuth session processing error:', error);

      let message = 'Failed to process OAuth session';
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }

      return { success: false, error: message };
    }
  };

  const value = {
    user,
    signup,
    login,
    logout,
    showAuthModal,
    authMode,
    openAuthModal,
    closeAuthModal,
    isAuthenticated,
    loading,
    getMyClaims,
    claimDeal,
    checkDealClaim,
    getMyDealClaims,
    updateProfile,
    refreshUser,
    processOAuthSession,
    postAuthRedirect,
    setPostAuthRedirect,
    clearPostAuthRedirect,
    // Preview mode claim handling
    pendingClaim,
    setPendingClaim,
    clearPendingClaim,
    completePendingClaim,
    // Role helpers
    isAdmin,
    isAdvertiser,
    isConsumer,
    hasRole,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
